import React from 'react';
import { PropTypes } from 'prop-types';

import { AnalyticsEvent, pushPlausibleEvent } from 'utils/analytics';

import { HoverConsumer, Consts as CursorConsts } from '../../custom-cursor';

import CarouselContent from './CarouselContent';

import { CarouselTypes } from './consts';

import { DynamicContainer } from '../Layout';
import LazyLoadContainer from '../LazyLoad';

const Carousel = ({
    styles,
    rules,
    content,
    type,
    title,
    context,
    setContext,
    fullscreen,
    galleryType,
    isHomePage,
    noPaddingBottom,
    noPaddingTop,
    titleAs,
    subtitleAs,
    withButtons,
    office,
    containerEventId,
    analyticsEvent,
}) => {
    const slides = content;

    // TODO add window resize function to update this
    const [width, setWidth] = React.useState(null);
    const containerRef = React.useRef(0);

    const containerWidth = containerRef.current.offsetWidth;

    React.useEffect(() => {
        setWidth(containerWidth);

        function handleResize() {
            setWidth(containerRef.current.offsetWidth);
        }

        window.addEventListener('resize', handleResize);

        return function cleanup() {
            window.removeEventListener('resize', handleResize);
        };
    }, [containerWidth]);

    const [state, setState] = React.useState({
        translate: 0,
        transitionDuration: 0.45,
        activeIndex: 0,
    });

    const { translate, transitionDuration, activeIndex } = state;

    const [isMoving, setIsMoving] = React.useState(false);

    const nextSlide = React.useCallback(
        (nextSlideId, device) => {
            // If next slide would be higher than the total slides retun back to slide 1
            if (nextSlideId >= slides.length) {
                nextSlideId = 0;
            }

            // If nex slide would be smaller then the first on go to last slide
            if (nextSlideId < 0) {
                nextSlideId = slides.length - 1;
            }

            setState({
                ...state,
                translate: nextSlideId * width * -1,
                activeIndex: nextSlideId,
            });

            setIsMoving(true);

            setTimeout(() => {
                setIsMoving(false);
            }, transitionDuration * 1000);
        },
        [state, width, slides.length, transitionDuration],
    );

    const moveTonextSlide = React.useCallback(() => nextSlide(activeIndex + 1), [activeIndex, nextSlide]);

    const [cursorIsOnTheLeft, setcursorIsOnTheLeft] = React.useState(false);

    const handleMouseMove = e => {
        const containerRect = containerRef.current.getBoundingClientRect();
        const mousePositionX = e.clientX - containerRect.left;

        if (mousePositionX > width / 2) {
            if (!cursorIsOnTheLeft) return;
            setcursorIsOnTheLeft(false);
            setContext({
                ...context,
                carousel: {
                    ...context.carousel,
                    direction: CursorConsts.CarouselDirections.RIGHT,
                },
            });
        } else {
            if (cursorIsOnTheLeft) return;
            setcursorIsOnTheLeft(true);
            setContext({
                ...context,
                carousel: {
                    ...context.carousel,
                    direction: CursorConsts.CarouselDirections.LEFT,
                },
            });
        }
    };

    const handleContainerClick = () => {
        if (!cursorIsOnTheLeft) {
            // mouse is on the right side
            nextSlide(activeIndex + 1);
        }
        if (cursorIsOnTheLeft) {
            // mouse is on the left side
            nextSlide(activeIndex - 1);
        }
    };

    const handleHover = React.useCallback(
        hovered => {
            if (slides.length > 1) {
                let theme;
                if (type === CarouselTypes.SERVICES || type === CarouselTypes.PROCESS) {
                    theme = CursorConsts.CarouselThemes.DARK;
                } else {
                    theme = CursorConsts.CarouselThemes.LIGHT;
                }
                setContext({
                    ...context,
                    carousel: {
                        ...context.carousel,
                        hovered,
                        theme,
                    },
                });
            }
        },
        [context, type, setContext, slides.length],
    );

    const handleMouseEnter = React.useCallback(
        () => handleHover(true, context, setContext, type),
        [handleHover, setContext, type, context],
    );

    const handleMouseLeave = React.useCallback(
        () => handleHover(false, context, setContext, type),
        [handleHover, setContext, type, context],
    );

    const handleMouseHover = React.useCallback(
        value => handleHover(value, context, setContext, type),
        // eslint-disable-next-line
        [type],
    );

    let eventHandlers;

    // todo refactor this to only be based on withButtons
    if (!withButtons) {
        switch (type) {
            case CarouselTypes.SERVICES:
            case CarouselTypes.CASE_STUDIES:
            case CarouselTypes.WORKSHOP_PROCESS:
                eventHandlers = null;
                break;

            default:
                eventHandlers = {
                    onMouseEnter: handleMouseEnter,
                    onMouseLeave: handleMouseLeave,
                    onMouseMove: handleMouseMove,
                    onClick: handleContainerClick,
                    onKeyPress: handleContainerClick,
                };
                break;
        }
    }

    return (
        // TODO: add context to reduce unnecessary prop drilling
        // eslint-disable-next-line react/jsx-props-no-spreading
        <div {...{ tabIndex: '0', role: 'button', className: styles.mainContainer, ...eventHandlers }}>
            <DynamicContainer
                office={office}
                carouselType={type}
                extend={rules.extendContainer}
                // reference={reference}
                isFluid={!fullscreen}
                isServices={type === CarouselTypes.SERVICES}
                isHomePage={isHomePage}
                noPaddingBottom={noPaddingBottom}
                noPaddingTop={noPaddingTop}
            >
                <div
                    ref={containerRef}
                    data-event-id={containerEventId}
                    className={styles.container}
                    role="presentation"
                    onClick={() => {
                        pushPlausibleEvent(analyticsEvent, {
                            carousel_type: containerEventId,
                            current_carousel_slide: `${activeIndex + 1};${slides.length}`,
                        });
                    }}
                >
                    {width && (
                        <>
                            <CarouselContent
                                slides={slides}
                                width={width}
                                translate={translate}
                                transitionDuration={transitionDuration}
                                isMoving={isMoving}
                                moveToSlideId={nextSlide}
                                moveToNextSlide={moveTonextSlide}
                                moveToNextSlideMobile={moveTonextSlide}
                                activeIndex={activeIndex}
                                type={type}
                                title={title}
                                galleryType={galleryType}
                                handleHover={handleMouseHover}
                                titleAs={titleAs}
                                subtitleAs={subtitleAs}
                                withButtons={withButtons}
                            />
                        </>
                    )}
                </div>
            </DynamicContainer>
        </div>
    );
};

Carousel.propTypes = {
    styles: PropTypes.shape().isRequired,
    rules: PropTypes.shape().isRequired,
    content: PropTypes.arrayOf(PropTypes.shape).isRequired,
    type: PropTypes.oneOf(Object.values(CarouselTypes)),
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.element]),
    context: PropTypes.shape().isRequired,
    setContext: PropTypes.func.isRequired,
    reference: PropTypes.shape(),
    fullscreen: PropTypes.bool,
    galleryType: PropTypes.string,
    isHomePage: PropTypes.bool,
    noPaddingBottom: PropTypes.bool,
    noPaddingTop: PropTypes.bool,
    titleAs: PropTypes.string,
    subtitleAs: PropTypes.string,
    withButtons: PropTypes.bool,
    containerEventId: PropTypes.string,
    analyticsEvent: PropTypes.oneOf(Object.values(AnalyticsEvent)),
};

Carousel.defaultProps = {
    type: CarouselTypes.CASE_STUDIES,
    title: null,
    reference: null,
    fullscreen: false,
    galleryType: '',
    isHomePage: false,
    titleAs: undefined,
    subtitleAs: undefined,
    noPaddingBottom: undefined,
    noPaddingTop: undefined,
    withButtons: false,
    containerEventId: undefined,
    analyticsEvent: AnalyticsEvent.CAROUSEL_CLICK,
};

const CarouselWrapper = props => {
    const Section = () => (
        <section ref={props.reference}>
            <LazyLoadContainer lazyLoaded={props.lazyLoaded}>
                <HoverConsumer>
                    {render => <Carousel context={render.context} setContext={render.setContext} {...props} />}
                </HoverConsumer>
            </LazyLoadContainer>
        </section>
    );

    return <Section />;
};

CarouselWrapper.propTypes = {
    reference: PropTypes.shape(),
    lazyLoaded: PropTypes.bool,
};

CarouselWrapper.defaultProps = {
    reference: null,
    lazyLoaded: false,
};

export default CarouselWrapper;
